/**
 * consoleScreens — the shipped Console screen registry: ids, nav labels, and the
 * lazy component behind each.
 *
 * Its own module rather than an export from `ConsoleShell.jsx`, for the reason
 * `argus-dashboard/src/lib/tabs.js` gives: a non-component export from a component
 * file breaks Fast Refresh for the whole file, and the shell is worth keeping
 * refreshable. It is also now read by TWO consumers that must not disagree —
 * `App.jsx` builds one `<Route>` per entry, `ConsoleShell.jsx` builds one `<NavLink>`
 * per entry — so a single list is what keeps the nav and the route table in step.
 *
 * **The URL path IS the id.** `pathForScreen` is the only place that spells the
 * relationship, so there is one fact in one place; a screen whose URL must differ
 * from its id gets an explicit field added here and nowhere else. The ids are
 * already the customer-facing words (`submit`, `jobs`, `billing`), which is why
 * they make honest URLs without a translation table nobody would maintain.
 *
 * Adding a screen is two edits: an entry here, and its `TAB_PERMISSION`-equivalent
 * if one is ever introduced. The route and the nav link both follow from the entry.
 */

import { lazy } from "react";

export const SCREENS = [
  { id: "submit", l: "Submit Job", component: lazy(() => import("../tabs/SubmitTab.jsx")) },
  { id: "projects", l: "Projects", component: lazy(() => import("../tabs/ProjectsTab.jsx")) },
  // `routePath` overrides the id-is-the-path default, for the two screens whose URL
  // carries more than the screen. Both use an OPTIONAL trailing segment so the screen
  // keeps exactly one route rather than gaining a sibling.
  { id: "jobs", l: "Jobs", routePath: "jobs/:jobId?", component: lazy(() => import("../tabs/ConsoleJobsTab.jsx")) },
  // E21's Stripe credit top-up returns the customer to
  // `${origin}/billing/credit?topup=<id>&status=success|cancelled`
  // (backend/app/core/services/credit_topup.py). That makes /billing/credit a SIXTH
  // externally minted path, alongside the five in routePaths.js, and routing it is not
  // cosmetic: CreditPanel reads `topup` straight off window.location.search, so the query
  // string IS the payment outcome. Without this route the catch-all redirected to the
  // default screen and discarded the query, losing the outcome entirely.
  { id: "billing", l: "Billing", routePath: "billing/credit?", component: lazy(() => import("../tabs/BillingTab.jsx")) },
  { id: "account", l: "Account", component: lazy(() => import("../tabs/AccountTab.jsx")) },
  { id: "guide", l: "Guide", component: lazy(() => import("../tabs/GuideTab.jsx")) },
];

/** The screen a signed-in customer with a full session lands on. */
export const DEFAULT_SCREEN = "submit";

/**
 * The absolute URL path for a screen id, e.g. "jobs" -> "/jobs".
 *
 * Unknown ids fall back to the default screen rather than minting a path no route
 * serves: the consoleNav channel carries ids from six call sites, and a typo there
 * should land the customer on a real screen instead of the catch-all redirect.
 */
export function pathForScreen(screenId) {
  const known = SCREENS.some((s) => s.id === screenId);
  return `/${known ? screenId : DEFAULT_SCREEN}`;
}
